import { useQuery } from '@apollo/client/react'
import { ADVISOR_WIL_TOOL_SLUG, WIL_DISPLAY_NAME } from '@src/components/sco/Advisors/constants'
import { gql } from '@src/lib/gql'
import { useMemo } from 'react'

export const WIL_TOOL_QUERY = gql`
  query AdvisorWilTool($slug: String!) {
    scoTool(slug: $slug) {
      id
      toolId
      title
      iconUrl
      slug
      toolUserId
      toolUser {
        id
        userId
        firstName
        lastName
        fullName
        displayName
        slug
        pictureUrl
      }
    }
  }
`

export type WilToolUser = {
  userId?: string
  firstName?: string
  lastName?: string
  fullName?: string
  displayName?: string
  slug?: string
  pictureUrl?: string
}

/**
 * Loads the advisor-wil ScoTool by slug and derives display fields used by Ask Wil.
 */
export function useWilTool(slug: string = ADVISOR_WIL_TOOL_SLUG) {
  const toolQuery = useQuery(WIL_TOOL_QUERY, {
    variables: { slug },
  })

  const toolId = toolQuery.data?.scoTool?.toolId as string | undefined
  const toolIconUrl = toolQuery.data?.scoTool?.iconUrl as string | undefined
  const toolUserId =
    (toolQuery.data?.scoTool?.toolUserId as string | undefined) ||
    (toolQuery.data?.scoTool?.toolUser?.userId as string | undefined)
  // AI tool users render as lastName only (e.g. firstName "Startups.com" + lastName "Wil.ai").
  const wilToolUser = toolQuery.data?.scoTool?.toolUser as WilToolUser | undefined
  const wilDisplayName = useMemo(
    () =>
      wilToolUser?.lastName ||
      wilToolUser?.fullName ||
      wilToolUser?.displayName ||
      (toolQuery.data?.scoTool?.title as string | undefined) ||
      WIL_DISPLAY_NAME,
    [wilToolUser?.lastName, wilToolUser?.fullName, wilToolUser?.displayName, toolQuery.data?.scoTool?.title]
  )

  return {
    toolQuery,
    toolId,
    toolIconUrl,
    toolUserId,
    wilToolUser,
    wilDisplayName,
    loading: toolQuery.loading,
    error: toolQuery.error,
  }
}
